import { useState, useMemo } from 'react';
import { AutoComplete, Input, Typography, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Project, Risk, PortalUser } from '../../types';
import { useTenantContext } from '../../context/TenantContext';

const { Text } = Typography;

interface SearchOption {
  value: string;
  label: React.ReactNode;
  type: 'project' | 'risk' | 'user';
  data: Project | Risk | PortalUser;
}

interface SearchAutocompleteProps {
  placeholder?: string;
  style?: React.CSSProperties;
}

export function SearchAutocomplete({ placeholder = "Buscar projetos, riscos ou responsáveis", style }: SearchAutocompleteProps) {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { selectedTenantId, isInGlobalView } = useTenantContext();

  const debouncedSearch = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        performSearch(query);
      }, 300);
    };
  }, []);

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const tenantId = isInGlobalView ? undefined : selectedTenantId || undefined;
      const [projects, risks, users] = await Promise.all([
        api.getProjects({ q: query, limit: 5, tenant_id: tenantId }),
        api.getRisks(undefined, { q: query, limit: 5, tenant_id: tenantId }),
        api.listUsers({ search: query, limit: 5, active_only: true, approved_only: true, tenant_id: tenantId })
      ]);

      const searchOptions: SearchOption[] = [];

      // Add projects
      projects.projects.forEach(project => {
        searchOptions.push({
          value: `project-${project.id}`,
          label: (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
              <Space>
                <Text strong style={{ color: '#1f4bd8' }}>📁</Text>
                <Text strong>{project.title}</Text>
                {project.code && <Text type="secondary">({project.code})</Text>}
              </Space>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Projeto
              </Text>
            </div>
          ),
          type: 'project',
          data: project
        });
      });

      // Add risks
      risks.forEach(risk => {
        searchOptions.push({
          value: `risk-${risk.id}`,
          label: (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
              <Space>
                <Text strong style={{ color: '#ff4d4f' }}>⚠️</Text>
                <Text strong>{risk.title}</Text>
              </Space>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Risco
              </Text>
            </div>
          ),
          type: 'risk',
          data: risk
        });
      });

      // Add users
      users.forEach(user => {
        searchOptions.push({
          value: `user-${user.id}`,
          label: (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
              <Space>
                <Text strong style={{ color: '#52c41a' }}>👤</Text>
                <Text strong>{user.name}</Text>
                {user.role_title && <Text type="secondary">({user.role_title})</Text>}
              </Space>
              <Text type="secondary" style={{ fontSize: '12px', marginLeft: '24px' }}>
                Responsável
              </Text>
            </div>
          ),
          type: 'user',
          data: user
        });
      });

      setOptions(searchOptions);
    } catch (error) {
      console.error('Erro na busca:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (value.trim()) {
      debouncedSearch(value);
    } else {
      setOptions([]);
    }
  };

  const handleSelect = (_value: string, option: SearchOption) => {
    if (option.type === 'project') {
      const project = option.data as Project;
      navigate(`/projeto?code=${project.code || ''}&id=${project.id}`);
    } else if (option.type === 'risk') {
      const risk = option.data as Risk;
      navigate(`/projeto?code=&id=${risk.project_id}`);
    } else if (option.type === 'user') {
      // Go directly to user registry with pre-filled search
      const userName = (option.data as PortalUser).name;
      navigate(`/cadastros/responsaveis?search=${encodeURIComponent(userName)}`);
    }
    setSearchValue('');
    setOptions([]);
  };

  const groupedOptions = useMemo(() => {
    const groups = options.reduce((acc, option) => {
      if (!acc[option.type]) {
        acc[option.type] = [];
      }
      acc[option.type].push(option);
      return acc;
    }, {} as Record<string, SearchOption[]>);

    const result: any[] = [];

    if (groups.project?.length) {
      result.push({
        label: <Text strong style={{ color: '#1f4bd8' }}>📁 Projetos</Text>,
        options: groups.project
      });
    }

    if (groups.risk?.length) {
      result.push({
        label: <Text strong style={{ color: '#ff4d4f' }}>⚠️ Riscos</Text>,
        options: groups.risk
      });
    }

    if (groups.user?.length) {
      result.push({
        label: <Text strong style={{ color: '#52c41a' }}>👤 Responsáveis</Text>,
        options: groups.user
      });
    }

    return result;
  }, [options]);

  return (
    <AutoComplete
      value={searchValue}
      options={groupedOptions}
      onSearch={handleSearch}
      onSelect={handleSelect}
      style={{ maxWidth: 420, minWidth: 220, flex: 1, ...style }}
      filterOption={false}
      notFoundContent={searchValue && !loading ? "Nenhum resultado encontrado" : null}
    >
      <Input
        prefix={<SearchOutlined />}
        placeholder={placeholder}
        allowClear
        size="large"
      />
    </AutoComplete>
  );
}